import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { Btn, Card, StatusBadge } from "@/components/shared";
import { Check, X, FileDown, CheckCircle2 } from "lucide-react";

const money = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PublicBid() {
  const { token } = useParams();
  const [bid, setBid] = useState(null);
  const [err, setErr] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/bids/public/${token}`).then((r) => setBid(r.data)).catch(() => setErr(true));
  }, [token]);

  const respond = async (action) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/bids/public/${token}/respond`, { action });
      setBid((b) => ({ ...b, status: data.status }));
    } catch (e) { toast.error("Could not submit your response. Please try again."); }
    finally { setBusy(false); }
  };

  if (err) return <Shell><Card className="p-10 text-center text-zinc-400 font-mono">This estimate link is invalid or has expired.</Card></Shell>;
  if (!bid) return <Shell><div className="font-mono text-[#FF5722] animate-pulse text-center py-10">Loading estimate…</div></Shell>;

  const decided = bid.status === "approved" || bid.status === "declined";

  return (
    <Shell>
      <Card className="p-6 sm:p-8 animate-rise">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <img src="/sss-logo.png" alt="Swift Signings & Services LLC" className="w-11 h-11 border-2 border-black shadow-[3px_3px_0px_#000] object-cover" />
            <div><div className="font-head font-black uppercase text-white leading-none">Swift Signings</div><div className="font-mono text-[10px] text-[#FF5722] uppercase tracking-widest">& Services LLC</div></div>
          </div>
          <StatusBadge status={bid.status} />
        </div>

        <h1 className="font-head font-black uppercase text-2xl text-white tracking-tight">{bid.title}</h1>
        <div className="font-mono text-sm text-zinc-500 mt-1">{bid.client_name}{bid.property_address ? ` · ${bid.property_address}` : ""}</div>

        <div className="mt-6 border-2 border-zinc-800">
          <table className="w-full text-left text-sm">
            <thead><tr className="bg-zinc-900 border-b-2 border-[#FF5722] text-[#FF5722] font-mono text-xs uppercase">
              <th className="p-3">Item</th><th className="p-3 text-right">Qty</th><th className="p-3 text-right">Price</th><th className="p-3 text-right">Amount</th>
            </tr></thead>
            <tbody>
              {bid.line_items.map((l, i) => (
                <tr key={`${l.name}-${i}`} className="border-b border-zinc-800">
                  <td className="p-3 text-white">{l.name}</td>
                  <td className="p-3 text-right font-mono text-zinc-400">{l.quantity}</td>
                  <td className="p-3 text-right font-mono text-zinc-400">{money(l.unit_price)}</td>
                  <td className="p-3 text-right font-mono text-white">{money(l.quantity * l.unit_price)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 space-y-1 max-w-xs ml-auto text-sm">
          <Row label="Subtotal" value={money(bid.totals.subtotal)} />
          <Row label={`Tax (${bid.tax_rate}%)`} value={money(bid.totals.tax)} />
          <Row label="Fee" value={money(bid.fee)} />
          <div className="flex justify-between font-head font-black text-xl text-[#FF5722] pt-2 border-t-2 border-zinc-800"><span>TOTAL</span><span data-testid="public-bid-total">{money(bid.totals.total)}</span></div>
        </div>

        {bid.notes && <p className="mt-4 text-sm text-zinc-400"><span className="text-zinc-500 font-mono text-xs uppercase">Notes: </span>{bid.notes}</p>}

        {bid.photo_ids?.length > 0 && (
          <div className="mt-6" data-testid="public-bid-photos">
            <div className="font-mono text-xs uppercase tracking-widest text-[#FF5722] mb-2">Job Photos</div>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
              {bid.photo_ids.map((pid) => <img key={pid} src={`${API}/bids/public/${token}/photo/${pid}`} alt="" className="w-full aspect-square object-cover border-2 border-zinc-800" />)}
            </div>
          </div>
        )}

        <a href={`${API}/bids/public/${token}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-6 text-zinc-400 hover:text-[#FF5722] font-mono text-sm"><FileDown size={16} /> Download PDF</a>

        <div className="mt-8 pt-6 border-t-2 border-zinc-800">
          {decided ? (
            <div className={`flex items-center gap-2 font-head font-bold uppercase ${bid.status === "approved" ? "text-emerald-500" : "text-red-500"}`} data-testid="public-bid-decision">
              <CheckCircle2 size={20} /> {bid.status === "approved" ? "You approved this estimate — we'll send your invoice shortly." : "You have declined this estimate."}
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-3">
              <Btn data-testid="approve-bid-btn" onClick={() => respond("approve")} disabled={busy} className="flex-1 bg-emerald-500 hover:bg-emerald-600"><Check size={18} /> Approve Estimate</Btn>
              <Btn variant="danger" data-testid="decline-bid-btn" onClick={() => respond("decline")} disabled={busy} className="flex-1"><X size={18} /> Decline</Btn>
            </div>
          )}
        </div>
      </Card>
    </Shell>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between text-zinc-400"><span>{label}</span><span className="font-mono">{value}</span></div>;
}
function Shell({ children }) {
  return <div className="min-h-screen bg-[#09090b] py-8 px-4"><div className="max-w-2xl mx-auto">{children}</div></div>;
}
